import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { products } from '@/mocks/products';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import StickyQuoteBar from './components/StickyQuoteBar';
import ProductQuoteForm from './components/ProductQuoteForm';

export default function ProductPage() {
  const { slug } = useParams<{ slug: string }>();
  const [quoteOpen, setQuoteOpen] = useState(false);
  const product = products.find((p) => p.slug === slug);

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-black text-gray-900 mb-2">Product Not Found</h1>
          <Link to="/" className="text-yellow-600 hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const related = products.filter((p) => p.categorySlug === product.categorySlug && p.slug !== product.slug).slice(0, 4);

  return (
    <div className="min-h-screen font-sans bg-white pb-20">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-gray-50 border-b border-gray-100">
        <div className="max-w-[1420px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-xs text-gray-500 flex-wrap">
            <Link to="/" className="hover:text-blue-800">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/products/${product.categorySlug}`} className="hover:text-blue-800">{product.category}</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-800 font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Hero */}
      <section className="py-10 md:py-14">
        <div className="max-w-[1420px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-10 items-start">
            {/* Image */}
            <div className="w-full lg:w-1/2">
              <div className="w-full h-[280px] md:h-[400px] rounded-xl overflow-hidden bg-gray-100">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                {[
                  { icon: 'ri-shield-check-line', label: 'Mill Certified' },
                  { icon: 'ri-ship-line', label: 'FOB Shipping' },
                  { icon: 'ri-search-eye-line', label: 'SGS Inspected' },
                  { icon: 'ri-customer-service-2-line', label: '24/7 Support' },
                ].map((b) => (
                  <div key={b.label} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className={`${b.icon} text-sm`} style={{ color: '#0a2342' }} />
                    </div>
                    <span className="text-xs font-semibold text-gray-700">{b.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Info */}
            <div className="w-full lg:w-1/2">
              <span className="inline-block text-white text-xs font-bold px-3 py-1 rounded-full mb-3" style={{ backgroundColor: '#0a2342' }}>
                {product.category}
              </span>
              <h1 className="text-2xl md:text-4xl font-black text-gray-900 mb-2">{product.name}</h1>
              <p className="font-semibold text-sm mb-4 italic" style={{ color: '#1e5fa8' }}>{product.tagline}</p>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">{product.description}</p>

              {/* Specs */}
              <div className="bg-gray-50 rounded-xl p-5 mb-5">
                <h3 className="text-xs font-black text-gray-900 mb-3 uppercase tracking-widest">Specifications</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {product.specs.map((spec) => (
                    <div key={spec.label} className="flex gap-2 text-sm">
                      <span className="font-semibold text-gray-700 whitespace-nowrap">{spec.label}:</span>
                      <span className="text-gray-600">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Standards */}
              <div className="mb-6">
                <h3 className="text-xs font-black text-gray-900 mb-2 uppercase tracking-widest">Standards</h3>
                <div className="flex flex-wrap gap-2">
                  {product.standards.map((s) => (
                    <span key={s} className="px-3 py-1 text-white text-xs font-bold rounded-full" style={{ backgroundColor: '#0a2342' }}>{s}</span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setQuoteOpen(true)}
                  className="flex-1 text-white py-3.5 rounded-lg font-black text-sm transition-colors cursor-pointer whitespace-nowrap text-center"
                  style={{ backgroundColor: '#0a2342' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0d2d56'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#0a2342'; }}
                >
                  <i className="ri-file-list-3-line mr-2" />
                  Get a Quote for This Product
                </button>
                <a
                  href="https://wa.me/8617671262710"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 border-2 border-gray-900 text-gray-900 py-3.5 rounded-lg font-black text-sm hover:bg-gray-900 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg" /> WhatsApp Us
                </a>
              </div>

              <p className="text-xs text-gray-400 mt-3 text-center sm:text-left">
                <i className="ri-time-line mr-1" style={{ color: '#0a2342' }} />
                We typically respond within 2 hours during business hours (Mon–Fri, 9AM–6PM CST)
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-[1420px] mx-auto px-6">
          <h2 className="text-lg font-black text-gray-900 mb-5">Common Applications</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {product.applications.map((app) => (
              <div key={app} className="flex items-center gap-2 bg-white rounded-lg p-3 border border-gray-100">
                <div className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0" style={{ backgroundColor: '#0a2342' }}>
                  <i className="ri-check-line text-white text-xs" />
                </div>
                <span className="text-xs font-semibold text-gray-700">{app}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Buy From Us */}
      <section className="py-10 bg-white">
        <div className="max-w-[1420px] mx-auto px-6">
          <h2 className="text-lg font-black text-gray-900 mb-5">Why Buy From Steel Link Global?</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: 'ri-shield-check-line', title: 'Mill Certified', desc: 'All products come with mill test certificates' },
              { icon: 'ri-ship-line', title: 'FOB Shipping', desc: 'Flexible FOB terms from major Chinese ports' },
              { icon: 'ri-bank-card-line', title: 'L/C & TT', desc: 'We accept Letter of Credit and bank wire' },
              { icon: 'ri-customer-service-2-line', title: 'Dedicated Support', desc: 'Personal account manager for your orders' },
            ].map((item) => (
              <div key={item.title} className="flex gap-3 items-start p-4 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: '#0a2342' }}>
                  <i className={`${item.icon} text-white`} />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">{item.title}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{item.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {related.length > 0 && (
        <section className="py-10 bg-gray-50">
          <div className="max-w-[1420px] mx-auto px-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-black text-gray-900">Related Products</h2>
              <Link to={`/products/${product.categorySlug}`} className="text-xs font-bold flex items-center gap-1 whitespace-nowrap hover:opacity-80" style={{ color: '#0a2342' }}>
                View All <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/products/item/${p.slug}`}
                  className="group block bg-white rounded-xl overflow-hidden border border-gray-100 transition-all cursor-pointer"
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#1e5fa8'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = '#f3f4f6'; }}
                >
                  <div className="w-full h-[130px] md:h-[150px] overflow-hidden">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300" />
                  </div>
                  <div className="p-3">
                    <span className="text-xs font-bold text-gray-800 group-hover:text-blue-900 transition-colors leading-snug block">{p.name}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section className="py-12" style={{ backgroundColor: '#0a2342' }}>
        <div className="max-w-[1420px] mx-auto px-6 text-center">
          <h2 className="text-2xl font-black text-white mb-2">Ready to Order?</h2>
          <p className="text-gray-400 text-sm mb-6 max-w-lg mx-auto">Get a competitive FOB price from our mill partners within 2 hours.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => setQuoteOpen(true)}
              className="px-8 py-3 bg-white font-black rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              style={{ color: '#0a2342' }}
            >
              Request a Quote
            </button>
            <a
              href="https://wa.me/8617671262710"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-3 border-2 border-white/40 text-white font-black rounded-lg hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-whatsapp-line" /> WhatsApp Us
            </a>
          </div>
        </div>
      </section>

      <FooterSection />
      <StickyQuoteBar productName={product.name} onQuoteClick={() => setQuoteOpen(true)} />
      <ProductQuoteForm productName={product.name} isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}
